// ExploreModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  X,
  ChevronDown,
  ChevronUp,
  Rocket,
  Leaf,
  Waves,
  Coffee,
  Users,
  ShieldCheck,
  Hammer,
  PlusCircle,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "../../i18n";

const starterIconMap = {
  rocket: Rocket,
  leaf: Leaf,
  waves: Waves,
  coffee: Coffee,
  users: Users,
  hammer: Hammer,
  sparkles: Sparkles,
  shield: ShieldCheck,
};

const starterCategoryByIcon = {
  leaf: "stewardship",
  waves: "stewardship",
  coffee: "abundance",
  users: "abundance",
  hammer: "abundance",
  rocket: "ministry",
  sparkles: "ministry",
  shield: "ministry",
};

const toHtml = (value) => ({ __html: value });

export default function ExploreModal({ onClose, onJoinNow, isAuthenticated = false }) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);
  const modalRef = useRef(null);

  const getString = (key, fallback = "") => {
    const value = t(key);
    if (typeof value === "string" && value !== key) {
      return value;
    }
    return fallback || key;
  };

  const rawSections = t("exploreModal.sections");
  const sections = Array.isArray(rawSections) ? rawSections : [];

  const rawStarterItems = t("exploreModal.starterIdeas.items");
  const starterItems = Array.isArray(rawStarterItems) ? rawStarterItems : [];

  const modalTitle = getString("exploreModal.title", "Explore Communities");
  const ariaLabel = getString("exploreModal.ariaLabel", modalTitle);
  const closeLabel = getString("exploreModal.closeLabel", "Close");
  const heroBlurb = getString("exploreModal.heroBlurb");
  const starterTitle = getString(
    "exploreModal.starterIdeas.title",
    "Starter Ideas (steal these)"
  );
  const starterCategories = [
    {
      id: "stewardship",
      title: getString("exploreModal.starterIdeas.categories.stewardship.title", "Stewardship"),
      description: getString(
        "exploreModal.starterIdeas.categories.stewardship.description",
        "Care for land, food, water, and the created world."
      ),
    },
    {
      id: "abundance",
      title: getString("exploreModal.starterIdeas.categories.abundance.title", "Abundance"),
      description: getString(
        "exploreModal.starterIdeas.categories.abundance.description",
        "Build businesses and shared provision that strengthen people and communities."
      ),
    },
    {
      id: "ministry",
      title: getString("exploreModal.starterIdeas.categories.ministry.title", "Ministry"),
      description: getString(
        "exploreModal.starterIdeas.categories.ministry.description",
        "Reach, serve, gather, and encourage people in Christ."
      ),
    },
  ];
  const ctaHeading = isAuthenticated
    ? "Ready to build with your community?"
    : getString("exploreModal.cta.heading", "Ready to join—or start your own Community?");
  const ctaSubheading = isAuthenticated
    ? "Continue to your dashboard to find your groups and current work."
    : getString(
        "exploreModal.cta.subheading",
        "Sign in or sign up to create your profile and take the first step."
      );
  const talkToGuideText = isAuthenticated
    ? getString("profileMenu.dashboard", "Dashboard")
    : getString("exploreModal.cta.talkToGuide", "Talk with the Guide");
  const dismissText = getString("exploreModal.cta.dismiss", "Not now");

  // ESC to close
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  // Focus trap
  useEffect(() => {
    const root = modalRef.current;
    if (!root) return;
    const getFocusables = () =>
      Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));
    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const f = getFocusables();
      if (f.length === 0) return;
      const first = f[0],
        last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener("keydown", onKeyDown);
    return () => root.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur">
      <div
        ref={modalRef}
        className="relative w-full max-w-[min(92vw,40rem)] sm:max-w-3xl lg:max-w-4xl max-h-[85vh] overflow-y-auto bg-secondary/95 text-white rounded-2xl shadow-2xl border border-white/10"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-4 sm:px-6 py-4 border-b border-white/10 sticky top-0 bg-secondary/95 rounded-t-2xl z-10">
          <h2 className="text-2xl md:text-3xl font-bold text-center">{modalTitle}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-white/10 focus:outline-none absolute right-3 top-1/2 -translate-y-1/2"
            aria-label={closeLabel}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Hero blurb */}
        {heroBlurb && (
          <div className="px-4 sm:px-6 pt-5 pb-2 animate-fadeIn">
            <p className="text-white/90 text-sm sm:text-base text-center max-w-3xl mx-auto">{heroBlurb}</p>
          </div>
        )}

        {/* Accordion */}
        <div className="divide-y divide-white/10">
          {sections.map((sec, i) => {
            const isOpen = i === openIndex;
            return (
              <div key={`${sec.title}-${i}`}>
                <button
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  className="w-full relative grid grid-cols-[24px_1fr_24px] items-center px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/5 focus:outline-none"
                >
                  <span aria-hidden className="w-6 h-6" />
                  <span className="col-start-2 text-center font-semibold text-base sm:text-lg md:text-xl">
                    {sec.title}
                  </span>
                  <span className="col-start-3 justify-self-end">
                    {isOpen ? <ChevronUp /> : <ChevronDown />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-6 pb-5 text-sm sm:text-base text-white/90 space-y-3 animate-fadeIn">
                    {Array.isArray(sec.paragraphs) &&
                      sec.paragraphs.map((paragraph, idx) => (
                        <p
                          key={`paragraph-${idx}`}
                          className={idx === 0 ? "mb-3" : undefined}
                          dangerouslySetInnerHTML={toHtml(paragraph)}
                        />
                      ))}
                    {Array.isArray(sec.list) && (
                      <ul className="list-disc pl-5 space-y-2">
                        {sec.list.map((item, idx) => (
                          <li key={`item-${idx}`} dangerouslySetInnerHTML={toHtml(item)} />
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Starter ideas grid */}
        <div className="px-4 sm:px-6 py-6">
          <h3 className="text-xl md:text-2xl font-bold text-center mb-4">{starterTitle}</h3>
          <div className="space-y-7">
            {starterCategories.map((category) => {
              const categoryItems = starterItems.filter(
                (item) => (item.category || starterCategoryByIcon[item.icon]) === category.id
              );

              if (categoryItems.length === 0) return null;

              return (
                <section key={category.id} aria-labelledby={`starter-category-${category.id}`}>
                  <div className="mb-3 text-center sm:text-left">
                    <h4
                      id={`starter-category-${category.id}`}
                      className="text-lg sm:text-xl font-bold text-yellow-300"
                    >
                      {category.title}
                    </h4>
                    <p className="text-sm sm:text-base text-white/70">{category.description}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {categoryItems.map((item, idx) => {
                      const Icon = starterIconMap[item.icon];
                      return (
                        <div
                          key={`${item.title}-${idx}`}
                          className="bg-white/5 hover:bg-white/10 transition rounded-xl p-4 sm:p-5 ring-1 ring-white/10 shadow"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div className="p-2 rounded-lg bg-black/20">
                              {Icon ? <Icon className="w-5 h-5" /> : null}
                            </div>
                            <div className="font-semibold">{item.title}</div>
                          </div>
                          <p className="text-sm sm:text-base text-white/80 leading-relaxed">{item.blurb}</p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        {/* CTA band */}
        <div className="px-4 sm:px-6 pb-6">
          <div className="bg-olive/40 rounded-2xl p-5 flex flex-col md:flex-row items-center md:items-start justify-between gap-4 ring-1 ring-white/10 text-center md:text-left">
            <div className="space-y-1">
              <div className="text-lg font-semibold">{ctaHeading}</div>
              <div className="text-white/90 text-sm sm:text-base">{ctaSubheading}</div>
            </div>
            <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
              <button
                onClick={onJoinNow}
                className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-olive font-semibold px-5 py-3 rounded-xl shadow inline-flex items-center justify-center gap-2"
              >
                <PlusCircle className="w-5 h-5" />
                {talkToGuideText}
              </button>
              <button
                onClick={onClose}
                className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-xl border border-white/20"
              >
                {dismissText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
